import React, { createContext, useContext, useMemo, useCallback } from 'react';
import useEnvironments from 'hooks/queries/useEnvironments';
import useTenants from 'hooks/queries/useTenants';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { Environment, Tenant } from '../../types';
import { StorageKeys } from 'common/constants';

type AppContext = {
    state: {
        tenant: Tenant;
        environment: Environment;
    };
    isLoading: boolean;
    clear: () => void;
    availableTenants: Tenant[] | undefined;
    setTenant: (tenant: Tenant) => void;
    availableEnvironments: Environment[] | undefined;
    setEnvironment: (env: Environment) => void;
};

const AppContext = createContext<AppContext | undefined>(undefined);

const initialTenant: Tenant = {
    id: '',
    name: ''
};

const initialEnvironment = {
    id: '',
    iotHubName: '',
    dataCenter: '',
    resourceGroup: '',
    isProduction: false,
    allowNewEnrollment: false,
    tenants: []
};

export function AppContextProvider({
    children
}: {
    children: React.ReactNode;
}) {
    const { data: tenants } = useTenants();
    const [tenant, setTenant] = useLocalStorage<Tenant>(
        StorageKeys.tenant,
        initialTenant
    );
    const [environment, setEnvironment] = useLocalStorage<Environment>(
        StorageKeys.environment,
        initialEnvironment
    );

    const { data: environments, isLoading, isIdle } = useEnvironments({
        enabled: Boolean(tenants)
    });

    const availableEnvironments = useMemo(() => {
        const getEnvironmentsForSelectedTenant = () =>
            environments?.filter(env => {
                return env.tenants?.includes(tenant.id) && env.id !== 'tork-ble';
            });

        return environments ? getEnvironmentsForSelectedTenant() : [];
    }, [environments, tenant]);

    const clear = useCallback(() => {
        setEnvironment(initialEnvironment);
        setTenant(initialTenant);
    }, [setEnvironment, setTenant]);

    const state = useMemo(
        () => ({
            tenant,
            environment
        }),
        [tenant, environment]
    );

    return (
        <AppContext.Provider
            value={{
                state,
                availableTenants: tenants,
                availableEnvironments,
                setTenant,
                setEnvironment,
                isLoading: isIdle || isLoading,
                clear
            }}>
            {children}
        </AppContext.Provider>
    );
}

export const useAppContext = () => {
    const context = useContext(AppContext);

    if (context === undefined) {
        throw new Error(
            'useAppProvider must be used within an AppContextProvider'
        );
    }

    return context;
};
